import { useTranslation } from "react-i18next";
import { Inbox } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../ui/table";
import { cn } from "../../../../lib/utils";

export type SimpleTableColumn<T = any> = {
    header: React.ReactNode;
    cell: keyof T | ((row: T) => React.ReactNode);
    headerClassName?: string;
    className?: string;
};

type SimpleTableProps<T = any> = {
    columns: SimpleTableColumn<T>[];
    data: T[];
    className?: string;
    getRowKey?: (row: T, index: number) => React.Key;
};

function renderCell<T>(column: SimpleTableColumn<T>, row: T) {
    if (typeof column.cell === "function") return column.cell(row);
    return row[column.cell] as React.ReactNode;
}

function SimpleTable<T = any>({ columns, data, className, getRowKey }: SimpleTableProps<T>) {
    const { t } = useTranslation();

    return (
        <div className="tw-rounded-lg tw-border tw-overflow-auto [&>div]:tw-overflow-visible">
            <Table className={cn(className)}>
                {/* Solid, not translucent - body rows must not show through while scrolling. */}
                <TableHeader className="tw-sticky tw-top-0 tw-z-10 tw-bg-muted">
                    <TableRow className="tw-h-14 hover:tw-bg-transparent">
                        {columns.map((column, index) => (
                            <TableHead key={index} className={cn("tw-px-2", column.headerClassName)}>
                                {column.header}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data.length === 0 ? (
                        <TableRow className="hover:tw-bg-transparent">
                            <TableCell colSpan={columns.length}>
                                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-4 tw-text-gray-500">
                                    <Inbox />
                                    {t("no-data")}
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : data.map((row, rowIndex) => (
                        <TableRow
                            key={getRowKey ? getRowKey(row, rowIndex) : (row as any)?.id ?? rowIndex}
                            className="tw-h-12"
                        >
                            {columns.map((column, columnIndex) => (
                                <TableCell key={columnIndex} className={cn("tw-py-1", column.className)}>
                                    {renderCell(column, row)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

export default SimpleTable;
